import React, { useState } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import { addClinicService } from '../../services/api';

const AddClinicServiceModal = ({ isOpen, onClose, onSuccess, selectedService, clinicId }) => {
    const [formData, setFormData] = useState({
        price: '',
        duration: '',
        description: '',
        isActive: true,
        hasPackages: false
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.price || parseFloat(formData.price) <= 0) {
            newErrors.price = 'Valid price is required';
        }

        if (!formData.duration || parseInt(formData.duration) <= 0) {
            newErrors.duration = 'Valid duration is required';
        }

        if (formData.description && formData.description.trim().length < 5) {
            newErrors.description = 'Description must be at least 5 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const clinicServiceData = {
                clinicId: parseInt(clinicId),
                serviceId: selectedService.id,
                basePrice: parseFloat(formData.price),
                durationMinutes: parseInt(formData.duration),
                description: formData.description.trim() || null,
                isEnabled : formData.isActive,
                hasPackages: formData.hasPackages
            };

            const response = await addClinicService(clinicServiceData);
            
            if (response.success) {
                onSuccess(response.data);
                handleClose();
            } else {
                setErrors({ submit: response.message || 'Failed to add service to clinic' });
            }
        } catch (error) {
            setErrors({ submit: 'An unexpected error occurred' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            price: '',
            duration: '',
            description: '',
            isActive: true,
            hasPackages: false
        });
        setErrors({});
        setLoading(false);
        onClose();
    };

    if (!isOpen || !selectedService) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">Add Service to Clinic</h2>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Selected Service Info */}
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <div className="space-y-2">
                        <h3 className="font-medium text-gray-900">{selectedService.name}</h3>
                        <p className="text-sm text-gray-600">{selectedService.description}</p>
                        <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">Code: {selectedService.shortCode}</span>
                            {selectedService.category && (
                                <span className="text-xs text-gray-500">• {selectedService.category}</span>
                            )}
                            {selectedService.isMultiSession && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Multi-Session
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                    {/* Price */}
                    <div>
                        <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                            Price (₹) *
                        </label>
                        <Input
                            type="number"
                            id="price"
                            name="price"
                            value={formData.price}
                            onChange={handleInputChange}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            error={errors.price}
                            required
                        />
                    </div>

                    {/* Duration */}
                    <div>
                        <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
                            Duration (minutes) *
                        </label>
                        <Input
                            type="number"
                            id="duration"
                            name="duration"
                            value={formData.duration}
                            onChange={handleInputChange}
                            placeholder="60"
                            min="1"
                            error={errors.duration}
                            required
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            Clinic-Specific Description
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Any specific details about how this clinic provides this service..."
                            rows={3}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors.description ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        {errors.description && (
                            <p className="text-sm text-red-600 mt-1">{errors.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            Optional: Add clinic-specific details about this service
                        </p>
                    </div>

                    {/* Active Status */}
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="isActive"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                            Service is active and available for booking
                        </label>
                    </div>

                    {/* Package Discounts */}
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="hasPackages"
                            name="hasPackages"
                            checked={formData.hasPackages}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="hasPackages" className="ml-2 block text-sm text-gray-900">
                            This service offers package discounts
                        </label>
                    </div>
                    <p className="text-xs text-gray-500">
                        Check if this service has special pricing for multiple sessions or packages
                    </p>

                    {/* Submit Error */}
                    {errors.submit && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                            <p className="text-sm text-red-600">{errors.submit}</p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex space-x-3 pt-4">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleClose}
                            className="flex-1"
                            loading={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            className="flex-1"
                            loading={loading}
                        >
                            Add Service
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddClinicServiceModal; 